import { Locale, defaultLocale } from './i18n';
import { getTranslation } from './translations';

export interface RouteConfig {
  path: string;
  component: string;
}

export function getLocalizedRoute(routeKey: string, locale: Locale): string {
  const t = getTranslation(locale);
  
  if (locale === defaultLocale) {
    return t.routes[routeKey as keyof typeof t.routes] || routeKey;
  }
  
  return `/${locale}/${t.routes[routeKey as keyof typeof t.routes] || routeKey}`;
}

export function getAllLocalizedRoutes(routeKey: string): string[] {
  const routes: string[] = [];
  
  // Add default locale route (without locale prefix)
  const defaultT = getTranslation(defaultLocale);
  routes.push(`/${defaultT.routes[routeKey as keyof typeof defaultT.routes] || routeKey}`);
  
  // Add routes for other locales
  ['en', 'fr', 'it'].forEach(locale => {
    const t = getTranslation(locale as Locale);
    routes.push(`/${locale}/${t.routes[routeKey as keyof typeof t.routes] || routeKey}`);
  });
  
  return routes;
}

export function getNestedLocalizedRoutes(parentKey: string, childKey: string): string[] {
  const routes: string[] = [];
  
  // Add default locale route (without locale prefix)
  const defaultT = getTranslation(defaultLocale);
  const parentRoute = defaultT.routes[parentKey as keyof typeof defaultT.routes] || parentKey;
  const childRoute = defaultT.routes[childKey as keyof typeof defaultT.routes] || childKey;
  routes.push(`/${parentRoute}/${childRoute}`);
  
  // Add routes for other locales
  ['en', 'fr', 'it'].forEach(locale => {
    const t = getTranslation(locale as Locale);
    const parentRoute = t.routes[parentKey as keyof typeof t.routes] || parentKey;
    const childRoute = t.routes[childKey as keyof typeof t.routes] || childKey;
    routes.push(`/${locale}/${parentRoute}/${childRoute}`);
  });
  
  return routes;
}

// Helper function to get current locale from a localized path
export function getLocaleFromLocalizedPath(path: string): Locale {
  const segments = path.split('/').filter(segment => segment);
  
  if (segments.length === 0) return defaultLocale;
  
  const potentialLocale = segments[0];
  
  // Check if first segment is a locale
  if (['en', 'fr', 'it'].includes(potentialLocale)) {
    return potentialLocale as Locale;
  }
  
  return defaultLocale;
}

// Helper function to match a current path to a route key
export function getRouteKeyFromPath(path: string, locale: Locale): string | null {
  const t = getTranslation(locale);
  const pathWithoutLocale = path.replace(`/${locale}`, '').replace(/^\//, '');
  
  // Check each route to find a match
  for (const [key, route] of Object.entries(t.routes)) {
    if (route === pathWithoutLocale) {
      return key;
    }
  }
  
  return null;
}